import React, { useEffect, useRef, useState } from 'react';
import { useNavigate }                         from 'react-router-dom';
import { useAuth }                             from '../contexts/AuthContext';
import './PartsPage.css';   // shared pts-* utility classes
import {
  listVehicleImages,
  uploadVehicleImage,
  deleteVehicleImage,
  setPrimaryVehicleImage,
  aiFindVehiclePhoto,
} from '../api/parts';

export default function VehicleImagesPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState(null);
  useEffect(() => {
    const cached = localStorage.getItem('ar_active_vehicle');
    if (cached) { try { setVehicle(JSON.parse(cached)); } catch (_) {} }
  }, []);

  const [images,    setImages]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [uploading,   setUploading]   = useState(false);
  const [uploadErr,   setUploadErr]   = useState('');
  const [aiFinding,   setAiFinding]   = useState(false);
  const [aiFindMsg,   setAiFindMsg]   = useState('');

  const fileInputRef   = useRef(null);
  const cameraInputRef = useRef(null);

  const vehicleId = vehicle?.vehicleId;

  // ── Load images ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!vehicleId) { setLoading(false); return; }
    setLoading(true);
    listVehicleImages(vehicleId)
      .then(res => {
        if (res.success) setImages(res.data || []);
        else setError(res.message || 'Could not load images.');
      })
      .catch(() => setError('Could not load images.'))
      .finally(() => setLoading(false));
  }, [vehicleId]);

  // ── Upload handler ───────────────────────────────────────────────────────────
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';   // reset so same file can be re-selected

    setUploading(true);
    setUploadErr('');
    try {
      const isPrimary = images.length === 0 ? true : false;   // first image becomes primary
      const res = await uploadVehicleImage(vehicleId, file, { is_primary: isPrimary });
      if (res.success) {
        // Reload the full list to get the signed URL
        const fresh = await listVehicleImages(vehicleId);
        if (fresh.success) setImages(fresh.data || []);
      } else {
        setUploadErr(res.message || 'Upload failed.');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 413) {
        setUploadErr('Photo is too large for upload. Please reduce the camera resolution in your phone settings and try again.');
      } else {
        setUploadErr('Upload failed: ' + (err.response?.data?.message || err.message || 'please try again'));
      }
    } finally {
      setUploading(false);
    }
  }

  // ── AI Find Photo ────────────────────────────────────────────────────────────
  async function handleAiFind() {
    setAiFinding(true);
    setAiFindMsg('');
    setUploadErr('');
    try {
      const res = await aiFindVehiclePhoto(vehicleId);
      if (res.success) {
        setAiFindMsg(`Found: "${res.data?.caption || 'photo saved'}"`);
        // Reload the full list to get all signed URLs
        const fresh = await listVehicleImages(vehicleId);
        if (fresh.success) setImages(fresh.data || []);
      } else {
        setAiFindMsg('');
        setUploadErr(res.message || 'AI Find failed — no image located.');
      }
    } catch (err) {
      setUploadErr('AI Find failed: ' + (err.response?.data?.message || err.message || 'please try again'));
    } finally {
      setAiFinding(false);
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────────
  async function handleDelete(img) {
    if (!window.confirm(`Delete this image? This cannot be undone.`)) return;
    try {
      const res = await deleteVehicleImage(vehicleId, img.vehicleImageId);
      if (res.success) {
        setImages(prev => prev.filter(i => i.vehicleImageId !== img.vehicleImageId));
      } else {
        alert(res.message || 'Delete failed.');
      }
    } catch {
      alert('Delete failed — please try again.');
    }
  }

  // ── Set primary ──────────────────────────────────────────────────────────────
  async function handleSetPrimary(img) {
    try {
      const res = await setPrimaryVehicleImage(vehicleId, img.vehicleImageId);
      if (res.success) {
        setImages(prev =>
          prev.map(i => ({ ...i, is_primary: i.vehicleImageId === img.vehicleImageId ? 1 : 0 }))
            .sort((a, b) => b.is_primary - a.is_primary || new Date(a.created_at) - new Date(b.created_at))
        );
      } else {
        alert(res.message || 'Could not set primary.');
      }
    } catch {
      alert('Could not set primary — please try again.');
    }
  }

  const vehicleLabel = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`
    : 'Vehicle';

  return (
    <div className="pts-root">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header className="pts-header">
        <div className="pts-header-brand">
          <span className="pts-header-gear">⚙</span>
          <span className="pts-header-name">AutoRestoration</span>
        </div>
        <div className="pts-header-vehicle">
          {vehicle && (
            <span style={{ fontSize: '0.85rem', color: '#aaa' }}>{vehicleLabel}</span>
          )}
        </div>
        <div className="pts-header-user">
          <span className="pts-username">@{user?.username}</span>
          <button className="pts-signout-btn" onClick={() => { signOut(); navigate('/'); }}>Sign Out</button>
        </div>
      </header>

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <div className="pts-body">

        <button className="pts-back-dash" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>

        <div className="pts-page-title-row">
          <div className="pts-breadcrumb">
            <span className="pts-crumb pts-crumb-active">📷 Vehicle Images</span>
          </div>

          {/* Upload buttons */}
          {vehicleId && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="pts-add-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || aiFinding}
              >
                {uploading ? 'Uploading…' : '📁 Add from File'}
              </button>
              <button
                className="pts-add-btn"
                style={{ background: '#1a4a2e' }}
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading || aiFinding}
              >
                📸 Take Photo
              </button>
              <button
                className="pts-add-btn"
                style={{ background: '#2a1a4a' }}
                onClick={handleAiFind}
                disabled={uploading || aiFinding}
                title="Ask AI to find and save a reference photo for this vehicle"
              >
                {aiFinding ? '🤖 Searching…' : '🤖 AI Find'}
              </button>
            </div>
          )}
        </div>

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFile}
        />

        {uploadErr && (
          <div className="pts-error" style={{ marginBottom: 12 }}>{uploadErr}</div>
        )}
        {aiFindMsg && (
          <div style={{
            background: '#1a3a2e', border: '1px solid #3db89a', borderRadius: 6,
            padding: '8px 14px', marginBottom: 12, color: '#3db89a', fontSize: '0.88rem'
          }}>
            ✓ {aiFindMsg}
          </div>
        )}

        {!vehicleId && (
          <div className="pts-empty">
            <div className="pts-empty-icon">🚗</div>
            <h3 className="pts-empty-title">No active vehicle</h3>
            <p className="pts-empty-text">Select a vehicle from My Projects first.</p>
          </div>
        )}

        {vehicleId && loading && (
          <div className="pts-loading">Loading images…</div>
        )}
        {vehicleId && !loading && error && (
          <div className="pts-error">{error}</div>
        )}

        {vehicleId && !loading && !error && images.length === 0 && (
          <div className="pts-empty">
            <div className="pts-empty-icon">📷</div>
            <h3 className="pts-empty-title">No images yet</h3>
            <p className="pts-empty-text">
              Use "Add from File" to upload from your device, or "Take Photo" to use your camera.
            </p>
          </div>
        )}

        {vehicleId && !loading && images.length > 0 && (
          <>
            <div className="pts-parts-header-row">
              <span className="pts-parts-count">
                {images.length} image{images.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Image grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 16,
              marginTop: 8,
            }}>
              {images.map(img => (
                <div key={img.vehicleImageId} style={{
                  background: '#1e2839',
                  border: img.is_primary ? '2px solid #3db89a' : '1px solid #2e3a4a',
                  borderRadius: 10,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  {/* Thumbnail */}
                  <div style={{ position: 'relative', paddingTop: '66%', background: '#1a2236' }}>
                    {img.url ? (
                      <img
                        src={img.url}
                        alt={img.caption || img.original_filename || 'Vehicle photo'}
                        style={{
                          position: 'absolute', top: 0, left: 0,
                          width: '100%', height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <div style={{
                        position: 'absolute', top: 0, left: 0,
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#555', fontSize: '2rem',
                      }}>
                        📷
                      </div>
                    )}
                    {img.is_primary === 1 && (
                      <span style={{
                        position: 'absolute', top: 6, left: 6,
                        background: '#3db89a', color: '#fff',
                        fontSize: '0.7rem', padding: '2px 6px',
                        borderRadius: 4, fontWeight: 700,
                      }}>
                        PRIMARY
                      </span>
                    )}
                  </div>

                  {/* Card footer */}
                  <div style={{ padding: '8px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {img.caption && (
                      <div style={{ fontSize: '0.82rem', color: '#ccc' }}>{img.caption}</div>
                    )}
                    <div style={{ fontSize: '0.72rem', color: '#666' }}>
                      {img.original_filename}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                      {img.is_primary !== 1 && (
                        <button
                          onClick={() => handleSetPrimary(img)}
                          style={{
                            flex: 1, padding: '4px 0', fontSize: '0.75rem',
                            background: '#1a3a2e', color: '#3db89a',
                            border: '1px solid #3db89a', borderRadius: 4, cursor: 'pointer',
                          }}
                        >
                          ★ Set Primary
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(img)}
                        style={{
                          flex: img.is_primary === 1 ? 1 : 0,
                          padding: '4px 8px', fontSize: '0.75rem',
                          background: '#2a1a30', color: '#e07070',
                          border: '1px solid #e07070', borderRadius: 4, cursor: 'pointer',
                        }}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
